import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { Container, Button, Form } from "react-bootstrap";
import apiClient from "../Login/apiClient";
import { ToastContainer, toast } from "react-toastify";

const FIELD_KEYS = {
  id: "無謀な夢のID",
  title: "無謀な夢",
  deadline: "目標実現日",
  image: "目標イメージ画像",
  category: "カテゴリ",
  tags: "タグ",
  description: "無謀な夢の詳細",
};

const CATEGORY_MAP = {
  スポーツ: "sports",
  会社: "company",
  起業: "entrepreneurship",
  恋愛: "romance",
  自己満足: "self_satisfaction",
  "漫画、文学": "literature",
  sports: "sports",
  company: "company",
  entrepreneurship: "entrepreneurship",
  romance: "romance",
  self_satisfaction: "self_satisfaction",
  literature: "literature",
};

const CATEGORY_LABEL_MAP = {
  sports: "スポーツ",
  company: "会社",
  entrepreneurship: "起業",
  romance: "恋愛",
  self_satisfaction: "自己満足",
  literature: "漫画、文学",
};

CATEGORY_MAP["職業・キャリア"] = "career";
CATEGORY_MAP["資格・勉強"] = "study";
CATEGORY_MAP.career = "career";
CATEGORY_MAP.study = "study";

CATEGORY_LABEL_MAP.career = "職業・キャリア";
CATEGORY_LABEL_MAP.study = "資格・勉強";

const StyledContainer = styled(Container)`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 25px 12px;
  background-color: #f5f5f5;
  overflow-x: hidden;
`;

const ConfirmForm = styled(Form)`
  width: 100%;
  max-width: 500px;
  padding: 30px;
  border-radius: 10px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  background-color: white;
  box-sizing: border-box;

  .form-control[readonly] {
    font-size: 16px;
    cursor: default;
    pointer-events: none;
  }

  .form-control[readonly]:focus {
    box-shadow: none;
  }

  img {
    width: 100%;
    max-width: 320px;
    height: auto;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    border-radius: 10px;
    display: block;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    width: 90%;
    padding: 20px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const ConfirmButton = styled(Button)`
  width: 45%;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ChipReadOnly = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 20px;
  color: #0072bc;
  background-color: #d5e5f2;
  font-size: 12px;
  border-radius: 5px;
`;

const StyledTextarea = styled(Form.Control)`
  width: 100%;
  max-width: 100%;
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  height: 500px;
  resize: none;
  box-sizing: border-box;
  background-color: white;
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const readOnlyInputStyle = {
  backgroundColor: "#e9ecef",
  color: "#6c757d",
  cursor: "not-allowed",
};

const primaryButtonStyle = {
  minWidth: "80px",
  whiteSpace: "nowrap",
  fontSize: "1.2rem",
  backgroundColor: "#282c34",
  color: "#ffffff",
  borderColor: "#282c34",
};

const ConfirmPage = () => {
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const eventDetails = location.state?.eventDetails || null;

  useEffect(() => {
    if (!eventDetails) {
      navigate("/MyPage", {
        replace: true,
        state: { message: "編集内容が見つからなかったので、もう一度やり直してください" },
      });
    }
  }, [eventDetails, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tags = useMemo(() => {
    const rawTags = eventDetails?.[FIELD_KEYS.tags];
    if (Array.isArray(rawTags)) return rawTags;
    if (typeof rawTags === "string" && rawTags.trim()) {
      return rawTags
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean);
    }
    return [];
  }, [eventDetails]);

  if (!eventDetails) {
    return null;
  }

  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const tokenPayload = JSON.parse(localStorage.getItem("ResponseData") || "{}");
  const accessToken = tokenPayload?.access || "";

  const id = eventDetails[FIELD_KEYS.id] || "";
  const title = eventDetails[FIELD_KEYS.title] || "";
  const deadline = eventDetails[FIELD_KEYS.deadline] || "";
  const description = eventDetails[FIELD_KEYS.description] || "";
  const imageUrl = eventDetails[FIELD_KEYS.image] || "";
  const rawCategory = eventDetails[FIELD_KEYS.category] || "";
  const category = CATEGORY_MAP[rawCategory] || "";
  const categoryLabel = CATEGORY_LABEL_MAP[rawCategory] || rawCategory;

  const handleRegister = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      await apiClient.put(
        `${apiBaseUrl}/api/goals/${id}/`,
        {
          title,
          deadline,
          category,
          tags,
          description,
          image_url: imageUrl,
        },
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      navigate("/EditGoalRegisterCompleteView", { state: { eventDetails } });
    } catch (error) {
      const errorCode = error?.response?.status;

      if (errorCode === 401) {
        toast.warn("認証情報が正しく設定されていません。再度ログインしてください");
      } else if (errorCode === 400) {
        toast.info("入力内容に誤りがあります");
      } else if (errorCode === 500) {
        toast.error("サーバーエラーが発生しました。しばらくしてから再度お試しください");
      } else {
        toast.error("予期せぬエラーが発生しました");
      }

      setSubmitting(false);
    }
  };

  return (
    <StyledContainer>
      <ConfirmForm onSubmit={handleRegister}>
        <Form.Group>
          <Form.Label>
            無謀な夢 <span className="badge bg-danger">必須</span>
          </Form.Label>
          <Form.Control readOnly value={title} style={readOnlyInputStyle} />
        </Form.Group>
        <br />

        <Form.Group>
          <Form.Label>
            目標実現日 <span className="badge bg-danger">必須</span>
          </Form.Label>
          <Form.Control readOnly value={deadline} style={readOnlyInputStyle} />
        </Form.Group>
        <br />

        <Form.Group>
          <Form.Label>
            カテゴリ <span className="badge bg-danger">必須</span>
          </Form.Label>
          <Form.Control readOnly value={categoryLabel} style={readOnlyInputStyle} />
        </Form.Group>
        <br />

        <Form.Group>
          <Form.Label>
            目標イメージ画像 <span className="badge bg-danger">必須</span>
          </Form.Label>
          <br />
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="goal-preview"
              style={{ width: "100%", maxWidth: "320px", height: "auto", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: "10px", display: "block", margin: "0 auto" }}
            />
          ) : (
            <Form.Control readOnly value="画像が選択されていません" style={readOnlyInputStyle} />
          )}
        </Form.Group>
        <br />

        <Form.Group>
          <Form.Label>
            タグ <span className="badge bg-secondary">任意</span>
          </Form.Label>
          <ChipsRow>
            {tags.length === 0 ? (
              <span style={{ color: "#6c757d" }}>タグは設定されていません</span>
            ) : (
              tags.map((tag, index) => <ChipReadOnly key={`${tag}-${index}`}>#{tag}</ChipReadOnly>)
            )}
          </ChipsRow>
        </Form.Group>
        <br />

        <Form.Group>
          <Form.Label>
            無謀な夢の詳細 <span className="badge bg-danger">必須</span>
          </Form.Label>
          <StyledTextarea as="textarea" readOnly value={description} style={readOnlyInputStyle} />
        </Form.Group>

        <h1> </h1>
        <ButtonContainer>
          <ConfirmButton
            variant="contained"
            size="lg"
            block
            onClick={() => navigate(-1)}
            className="w-90 text-center d-flex align-items-center justify-content-center"
            style={primaryButtonStyle}
          >
            戻る
          </ConfirmButton>
          <ConfirmButton
            type="submit"
            variant="primary"
            size="lg"
            block
            disabled={submitting}
            className="w-90 text-center d-flex align-items-center justify-content-center"
            style={{
              ...primaryButtonStyle,
              backgroundColor: submitting ? "#999" : "#282c34",
              borderColor: submitting ? "#999" : "#282c34",
            }}
          >
            {submitting ? "更新中..." : "夢を更新する"}
          </ConfirmButton>
        </ButtonContainer>
      </ConfirmForm>
      <ToastContainer position="top-center" autoClose={3000} />
    </StyledContainer>
  );
};

export default ConfirmPage;
